import React, { useEffect, useRef, useState } from 'react';
import { transliterate, hasDevanagari, EXAMPLES } from '../utils/nepaliTransliterate';

const STORAGE_KEY = 'nepaliTyping';

/**
 * A text input that can turn romanized typing into Devanagari as you type, so
 * "chiyaa" becomes चिया without a Nepali keyboard installed.
 *
 * The field stays an ordinary text box the whole time: the toggle can be turned
 * off, Devanagari can be pasted in, and anything the transliteration gets wrong
 * can be fixed by hand. Roman script carries less information than Devanagari
 * does, so it will not always guess right - being editable is what makes that
 * acceptable rather than infuriating.
 *
 * `defaultEnabled` is the state before the user has ever touched the toggle. A
 * box whose whole purpose is the Nepali name passes true: nobody opens that
 * field wanting to type English into it. Once they do use the toggle, their
 * choice is remembered and beats the default from then on.
 */
function NepaliTextInput({
  value = '',
  onChange,
  className = '',
  defaultEnabled = false,
  ...inputProps
}) {
  const [enabled, setEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      // Absent means "never chosen" - which is the default, not "off". Reading a
      // missing key as off is what would keep the Nepali box starting in English.
      return saved === null ? defaultEnabled : saved === '1';
    } catch {
      return defaultEnabled;
    }
  });
  const [showHelp, setShowHelp] = useState(false);

  // The value in the box is always `prefix + transliterate(roman)`. The roman
  // buffer holds what has been typed since the last edit we could not read back
  // as roman, which is what lets backspace walk it off one roman letter at a
  // time instead of stranding half-converted text.
  const prefixRef = useRef(value || '');
  const romanRef = useRef('');
  const lastEmitted = useRef(value || '');

  // The parent can replace the value out from under us - a form reset, or an
  // item being loaded for editing. Our buffers then describe text that is no
  // longer in the box, so they have to be rebuilt from what is.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      prefixRef.current = value || '';
      romanRef.current = '';
      lastEmitted.current = value || '';
    }
  }, [value]);

  const emit = (next) => {
    lastEmitted.current = next;
    onChange(next);
  };

  const handleToggle = () => {
    setEnabled((on) => {
      const next = !on;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Private mode - the preference just will not survive a reload.
      }
      // Whatever is already in the box counts as finished text either way.
      prefixRef.current = value || '';
      romanRef.current = '';
      return next;
    });
  };

  const handleChange = (e) => {
    const next = e.target.value;

    if (!enabled) {
      prefixRef.current = next;
      romanRef.current = '';
      emit(next);
      return;
    }

    const current = prefixRef.current + transliterate(romanRef.current);

    if (next.startsWith(current)) {
      // Ordinary typing - the new characters join the roman buffer.
      romanRef.current += next.slice(current.length);
    } else if (current.startsWith(next)) {
      // Backspace. One roman letter can produce several Devanagari characters,
      // so shrink the buffer until its output fits what is left.
      while (
        romanRef.current &&
        prefixRef.current.length + transliterate(romanRef.current).length > next.length
      ) {
        romanRef.current = romanRef.current.slice(0, -1);
      }
      if (!romanRef.current) prefixRef.current = next;
    } else if (hasDevanagari(next)) {
      // Pasted Devanagari, or an edit in the middle of some. Take it as final.
      prefixRef.current = next;
      romanRef.current = '';
    } else {
      // Roman text that is not a continuation - most often the user selected
      // everything and started over. Treat the lot as fresh roman input rather
      // than dropping them back into literal English.
      prefixRef.current = '';
      romanRef.current = next;
    }

    emit(prefixRef.current + transliterate(romanRef.current));
  };

  return (
    <div>
      <div className="relative">
        <input
          type="text"
          {...inputProps}
          value={value}
          onChange={handleChange}
          lang={enabled ? 'ne' : undefined}
          className={className}
          /* Inline rather than a pr-* class. Callers pass responsive padding
             like "px-3 sm:px-4", and the sm: variant is emitted inside a media
             query - later in the stylesheet than any base class here - so it
             would win above 640px and put the text back under the button. */
          style={{ ...(inputProps.style || {}), paddingRight: '3.25rem' }}
        />
        <button
          type="button"
          onClick={handleToggle}
          aria-pressed={enabled}
          title={
            enabled
              ? 'Nepali typing is on - type "chiyaa" to get चिया. Click for English.'
              : 'Type Nepali with English letters (chiyaa → चिया)'
          }
          className={`absolute right-2 top-1/2 -translate-y-1/2 h-9 w-10 rounded-lg text-sm font-bold transition-colors ${
            enabled
              ? 'bg-cafe-600 text-white shadow-sm hover:bg-cafe-700'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          {enabled ? 'अ' : 'A'}
        </button>
      </div>

      {enabled && (
        <div className="mt-1.5">
          <p className="text-xs text-gray-500">
            Nepali typing is on — type <span className="font-mono text-gray-700">chiyaa</span> to get{' '}
            <span className="font-medium text-gray-800">चिया</span>.{' '}
            <button
              type="button"
              onClick={() => setShowHelp((open) => !open)}
              className="font-medium text-cafe-600 hover:text-cafe-700 underline underline-offset-2"
            >
              {showHelp ? 'Hide examples' : 'Show examples'}
            </button>
          </p>

          {showHelp && (
            <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1.5">
                {EXAMPLES.map(([roman, nepali]) => (
                  <div key={roman} className="flex items-baseline gap-1.5 text-xs min-w-0">
                    <span className="font-mono text-gray-500 truncate">{roman}</span>
                    <span className="text-gray-300">→</span>
                    <span className="font-medium text-gray-800 truncate">{nepali}</span>
                  </div>
                ))}
              </div>
              {/* The two things that are not guessable from the examples: how
                  to get the hard consonants, and how to stop a cluster. */}
              <p className="text-xs text-gray-500 mt-2.5 pt-2.5 border-t border-gray-200">
                Capitals give the hard letters — <span className="font-mono">T</span> ट,{' '}
                <span className="font-mono">Th</span> ठ, <span className="font-mono">D</span> ड,{' '}
                <span className="font-mono">Dh</span> ढ, <span className="font-mono">N</span> ण,{' '}
                <span className="font-mono">Sh</span> ष. Double a vowel to lengthen it
                (<span className="font-mono">aa</span> ा, <span className="font-mono">ii</span> ी,{' '}
                <span className="font-mono">uu</span> ू). Anything that comes out wrong can be
                typed over by hand.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NepaliTextInput;
